import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useLocale } from "./LocaleContext";
import {
  singInRequest,
  getUserInfo,
  singnOutRequest,
  singInByGoogle,
  getAdminOrganizations,
} from "../modules/signIn/api/index";
import { singInRequestAdmin } from "../modules/Admin/signIn/api/index";

export const AuthContext = createContext({
  isLoading: false,
  isAuth: false,
  setIsAuth: (b) => {},
  googleAuth: (userData) => {},
  onLogin: async (userData) => {},
  onLoginAdmin: async (userData) => {},
  onLogout: async () => {},
  fetchMe: async () => {},
  userInfo: {},
});

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const { setErrorModalShow } = useLocale();
  const [isAuth, setIsAuth] = useState(
    !!window.localStorage.getItem("accessToken"),
  );
  const [isLoading, setIsloading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const login = async (userData) => {
    try {
      setIsloading(true);
      const data = await singInRequest(userData, setErrorModalShow);
      const { access_token } = data;

      if (access_token) {
        window.localStorage.setItem("accessToken", access_token);
        setIsAuth(true);
        await fetchMe();
        return data;
      }
    } catch (error) {
      console.error("Login error:", error);
      setIsAuth(false);
      throw error.response?.data || error;
    } finally {
      setIsloading(false);
    }
  };
  const loginAdmin = async (userData) => {
    try {
      setIsloading(true);
      const data = await singInRequestAdmin(userData, setErrorModalShow);

      const { access_token } = data;

      if (access_token) {
        window.localStorage.setItem("accessToken", access_token);
        setIsAuth(true);
        return data;
      }
    } catch (error) {
      console.error("Login Admin error:", error);
      setIsAuth(false);
      throw error.response?.data || error;
    } finally {
      setIsloading(false);
    }
  };

  const googleAuth = async (userData) => {
    try {
      setIsloading(true);
      const token = await singInByGoogle(userData);

      if (token) {
        window.localStorage.setItem("accessToken", token);
        setIsAuth(true);
        return token;
      }
    } catch (error) {
      console.error("Google auth error:", error);
      setIsAuth(false);
      throw error.response?.data || error;
    } finally {
      setIsloading(false);
    }
  };
  const getAdminOrgs = async () => {
    try {
      setIsloading(true);
      const data = await getAdminOrganizations(setErrorModalShow);
      return data;
    } catch (error) {
      console.error("Get Admin Organizations error:", error);
      throw error.response?.data || error;
    } finally {
      setIsloading(false);
    }
  };
  const fetchMe = useCallback(async () => {
    try {
      setIsloading(true);
      const data = await getUserInfo(setErrorModalShow);
      if (data) {
        setUserInfo(data);
        setIsAuth(true);
        return data;
      } else {
        setIsAuth(false);
        window.localStorage.removeItem("accessToken");
      }
    } catch (error) {
      setIsAuth(false);
      setUserInfo(null);
      window.localStorage.removeItem("accessToken");
      throw error;
    } finally {
      setIsloading(false);
    }
  }, []);

  const logout = async () => {
    try {
      setIsloading(true);
      await singnOutRequest();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUserInfo(null);
      setIsAuth(false);
      window.localStorage.removeItem("accessToken");
      setIsloading(false);
    }
  };

  useEffect(() => {
    const token = window.localStorage.getItem("accessToken");
    if (token) {
      fetchMe();
      getAdminOrgs();
    }
  }, [fetchMe]);

  const value = {
    isLoading,
    isAuth,
    setIsAuth,
    onLogin: login,
    onLoginAdmin: loginAdmin,
    onLogout: logout,
    userInfo,
    fetchMe,
    googleAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
